// @ts-expect-error md5 has no bundled types
import md5 from 'md5';
import {
  isAllowedPlaceholderCheckHost,
  isComingSoonBySourceUrl,
  normalizeToCanonicalAssetUrl,
  PLACEHOLDER_IMAGE_MD5_HEX,
} from '~/utils/comingSoonFingerprint';

export const COMING_SOON_PLACEHOLDER = '/images/comingsoon.svg';

// Only thrivestate CDN images can match the placeholder MD5 set, so byte-fetching
// any other host (geniemap, googleapis, etc.) is wasted work that also trips CORS
// and a 403 from /api/placeholder-image-check. Skip the hash path for those.
const canHashImageHost = (rawUrl: string): boolean => {
  try {
    return isAllowedPlaceholderCheckHost(new URL(rawUrl).hostname);
  } catch {
    return false;
  }
};

const IMAGE_HASH_CACHE_KEY = 'image_hash_cache_v2';
const CACHE_EXPIRY_DAYS = 30;

interface ImageHashCache {
  [url: string]: {
    hash: string;
    isComingSoon: boolean;
    timestamp: number;
  };
}

const getCache = (): ImageHashCache => {
  if (typeof window === 'undefined') return {};
  try {
    const cached = localStorage.getItem(IMAGE_HASH_CACHE_KEY);
    if (!cached) return {};
    const parsed = JSON.parse(cached) as ImageHashCache;
    const now = Date.now();
    const expiry = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return Object.fromEntries(Object.entries(parsed).filter(([, value]) => now - value.timestamp < expiry));
  } catch {
    return {};
  }
};

const setCache = (cache: ImageHashCache) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(IMAGE_HASH_CACHE_KEY, JSON.stringify(cache));
  } catch {}
};

const resolveIsComingSoon = (hash: string, originalUrl: string, canonicalUrl: string): boolean => {
  return (
    PLACEHOLDER_IMAGE_MD5_HEX.has(hash) || isComingSoonBySourceUrl(originalUrl) || isComingSoonBySourceUrl(canonicalUrl)
  );
};

const writeCacheEntries = (
  cache: ImageHashCache,
  originalUrl: string,
  canonicalUrl: string,
  hash: string,
  isComingSoon: boolean,
) => {
  const ts = Date.now();
  cache[canonicalUrl] = { hash, isComingSoon, timestamp: ts };
  if (canonicalUrl !== originalUrl) {
    cache[originalUrl] = { hash, isComingSoon, timestamp: ts };
  }
};

const hashViaServer = async (
  originalUrl: string,
  canonicalUrl: string,
): Promise<{ hash: string; isComingSoon: boolean } | null> => {
  try {
    const data = await $fetch<{ hash: string; isComingSoon: boolean }>('/api/placeholder-image-check', {
      method: 'POST',
      body: { url: originalUrl },
    });
    if (typeof data?.hash !== 'string') return null;
    const updatedCache = getCache();
    writeCacheEntries(updatedCache, originalUrl, canonicalUrl, data.hash, Boolean(data.isComingSoon));
    setCache(updatedCache);
    return { hash: data.hash, isComingSoon: Boolean(data.isComingSoon) };
  } catch {
    return null;
  }
};

const hashImageContent = async (imageUrl: string): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  const canonicalUrl = normalizeToCanonicalAssetUrl(imageUrl);
  const cache = getCache();

  if (cache[canonicalUrl]?.hash) {
    return cache[canonicalUrl].hash;
  }

  if (!canHashImageHost(canonicalUrl) && !canHashImageHost(imageUrl)) {
    const updatedCache = getCache();
    writeCacheEntries(updatedCache, imageUrl, canonicalUrl, 'unsupported-host', false);
    setCache(updatedCache);
    return null;
  }

  try {
    const response = await fetch(canonicalUrl, { mode: 'cors' });
    if (!response.ok) {
      const via = await hashViaServer(imageUrl, canonicalUrl);
      return via?.hash ?? null;
    }

    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const hash = md5(new Uint8Array(arrayBuffer));
    const isComingSoon = resolveIsComingSoon(hash, imageUrl, canonicalUrl);

    const updatedCache = getCache();
    writeCacheEntries(updatedCache, imageUrl, canonicalUrl, hash, isComingSoon);
    setCache(updatedCache);
    return hash;
  } catch {
    const via = await hashViaServer(imageUrl, canonicalUrl);
    return via?.hash ?? null;
  }
};

export const isComingSoonImage = (imageUrl: string | null | undefined): boolean => {
  if (!imageUrl) return true;
  if (isComingSoonBySourceUrl(imageUrl)) return true;

  const canonicalUrl = normalizeToCanonicalAssetUrl(imageUrl);
  if (isComingSoonBySourceUrl(canonicalUrl)) return true;

  const cache = getCache();
  const cached = cache[canonicalUrl] || cache[imageUrl];

  return cached?.isComingSoon ?? false;
};

export const checkComingSoonImage = async (imageUrl: string | null | undefined): Promise<boolean> => {
  if (!imageUrl) return true;
  if (isComingSoonBySourceUrl(imageUrl)) return true;

  const canonicalUrl = normalizeToCanonicalAssetUrl(imageUrl);
  if (isComingSoonBySourceUrl(canonicalUrl)) return true;

  const cache = getCache();
  const cached = cache[canonicalUrl] || cache[imageUrl];

  if (cached?.isComingSoon !== undefined) {
    return cached.isComingSoon;
  }

  await hashImageContent(imageUrl);
  const after = getCache();
  const resolved = after[canonicalUrl] || after[imageUrl];
  return resolved?.isComingSoon ?? false;
};

export const getPropertyImageUrl = (imageUrl: string | null | undefined): string => {
  if (!imageUrl || isComingSoonImage(imageUrl)) {
    return COMING_SOON_PLACEHOLDER;
  }
  return imageUrl;
};

export const clearComingSoonCache = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(IMAGE_HASH_CACHE_KEY);
};
